import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReclutamientoService from '../../services/reclutamientoService.js';
import './ApplicationForm.css';
import MultipleSelect from '../MultipleSelect/MultipleSelect';
import TextForm from '../TextForm/TextForm';
import SelectForm from '../SelectForm/SelectForm';
import FileForm from '../FileForm/FileForm';
import Terms from '../Terms/Terms';
import Modal from '../Modal/Modal';
import i18next from 'i18next';

const ApplicationForm = ({ onSubmit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [errors, setErrors] = useState({});

  const languageOptions = [
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' },
    { value: 'ru', label: 'Русский' },
    { value: 'zh', label: '中文' },
    { value: 'ja', label: '日本語' },
    { value: 'ko', label: '한국어' },
    { value: 'ar', label: 'العربية' },
    { value: 'hi', label: 'हिन्दी' },
    { value: 'bn', label: 'বাংলা' },
    { value: 'nl', label: 'Nederlands' },
    { value: 'tr', label: 'Türkçe' },
    { value: 'sv', label: 'Svenska' },
    { value: 'pl', label: 'Polski' },
    { value: 'vi', label: 'Tiếng Việt' },
    { value: 'th', label: 'ไทย' },
    { value: 'id', label: 'Bahasa Indonesia' }
  ];

  const [formData, setFormData] = useState({
    nombre: '',
    edad: '',
    email: '',
    zona: '',
    disponibilidad: '',
    rol: '',
    experiencia: '',
    portafolio: '',
    archivo: null,
    idiomas: [],
    terms: false
  });

  const [showTerms, setShowTerms] = useState(false);

  // Manejar el scroll del body cuando el modal está abierto
  useEffect(() => {
    if (showTerms) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
  }, [showTerms]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    // Validación previa al submit
    const newErrors = {};
    if (!formData.nombre || formData.nombre.trim() === '') newErrors.nombre = t('form.validation.required', 'Este campo es obligatorio');
    if (!formData.edad || isNaN(Number(formData.edad)) || Number(formData.edad) < 18) newErrors.edad = t('form.validation.ageMin', 'Debes ser mayor de 18 años');
    const emailRegex = /.+@.+\..+/;
    if (!formData.email || !emailRegex.test(formData.email)) newErrors.email = t('form.validation.email', 'Ingresa un correo válido');
    if (!formData.zona || formData.zona.trim() === '') newErrors.zona = t('form.validation.required', 'Este campo es obligatorio');
    if (!formData.rol || String(formData.rol).trim() === '') newErrors.rol = t('form.validation.role', 'Selecciona un rol');
    if (!formData.experiencia || formData.experiencia.trim().length < 10) newErrors.experiencia = t('form.validation.experienceMin', 'Cuéntanos un poco más (mín. 10 caracteres)');
    if (!formData.idiomas || formData.idiomas.length === 0) newErrors.idiomas = t('form.validation.languagesMin', 'Selecciona al menos un idioma');
    if (!formData.terms) newErrors.terms = t('form.validation.termsRequired', 'Debes aceptar los términos');
    
    // Normalización de URL de portafolio (permitir sin https)
    let normalizedPortfolio = (formData.portafolio || '').trim();
    if (normalizedPortfolio) {
      if (!/^https?:\/\//i.test(normalizedPortfolio)) {
        normalizedPortfolio = `https://${normalizedPortfolio}`;
      }
      try {
        // Validación básica de URL
        // eslint-disable-next-line no-new
        new URL(normalizedPortfolio);
      } catch (_) {
        newErrors.portafolio = t('form.validation.portfolioInvalid', 'Ingresa una URL válida (ej: https://...)');
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }
    setErrors({});
    
    try {
      // Ejecutar reCAPTCHA (clave desde variables de entorno)
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        console.error('REACT_APP_RECAPTCHA_SITE_KEY no está definida');
      }
      if (!window.grecaptcha || typeof window.grecaptcha.execute !== 'function') {
        throw new Error('reCAPTCHA no se ha cargado. Intenta de nuevo en unos segundos.');
      }
      await new Promise((resolve) => window.grecaptcha.ready(resolve));
      const token = await window.grecaptcha.execute(siteKey, { action: 'submit' });
      
      // Obtener el idioma actual
      const currentLanguage = i18next.language || 'es';
      console.log("Idioma del formulario:", currentLanguage);
      
      // Agregar el token a los datos del formulario
      const formDataWithToken = {
        nombreCompleto: formData.nombre,
        edad: parseInt(formData.edad),
        email: formData.email,
        residencia: formData.zona,
        experiencia: formData.experiencia,
        portafolio: normalizedPortfolio || null,
        aceptaTerminos: formData.terms,
        recaptchaToken: token,
        rol: formData.rol,
        idiomas: formData.idiomas.map(lang => lang.value),
        disponibilidad: formData.disponibilidad || '',
        formLanguage: currentLanguage
      };
      
      // Crear el reclutamiento y obtener la respuesta
      const response = await ReclutamientoService.createReclutamiento(formDataWithToken);
      console.log('Reclutamiento creado exitosamente:', response);

      // Si hay un archivo seleccionado y tenemos el ID del reclutamiento, subirlo
      if (formData.archivo && response.id) {
        try {
          const fileFormData = new FormData();
          fileFormData.append('file', formData.archivo);
          
          await ReclutamientoService.uploadFile(response.id, fileFormData);
          console.log('Archivo subido exitosamente');
        } catch (fileError) {
          console.error('Error al subir el archivo:', fileError);
          setSubmitError(t('form.error.file'));
        }
      }
      
      // Si hay una función onSubmit proporcionada por el padre, la llamamos
      if (onSubmit) {
        onSubmit(formDataWithToken);
      }

      // Redirigimos a la pantalla de éxito con el email como parámetro de búsqueda
      navigate(`/submission-success?email=${encodeURIComponent(formData.email)}`);
      
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      const backendMsg = error?.response?.data?.message || error?.message;
      setSubmitError(backendMsg || t('form.error.submit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLanguageChange = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      idiomas: selectedOptions
    }));
  };

  const roleOptions = [
    {
      group: t('form.role.groups.science'),
      options: [
        { value: "neurociencia", text: t('form.role.options.neuroscience') },
        { value: "biometria", text: t('form.role.options.biometrics') },
        { value: "psicologia", text: t('form.role.options.psychology') },
        { value: "investigacion", text: t('form.role.options.research') }
      ]
    },
    {
      group: t('form.role.groups.tech'),
      options: [
        { value: "ml", text: t('form.role.options.ml') },
        { value: "nlp", text: t('form.role.options.nlp') },
        { value: "hardware", text: t('form.role.options.hardware') },
        { value: "wearables", text: t('form.role.options.wearables') },
        { value: "desarrollo_web", text: t('form.role.options.fullstack') },
        { value: "arquitectura_datos", text: t('form.role.options.data_architect') },
        { value: "devops", text: "Ingeniero DevOps" },
        { value: "integracion_sistemas", text: "Especialista en Integración de Sistemas y APIs" },
        { value: "ciberseguridad", text: "Especialista en Ciberseguridad" }
      ]
    },
    {
      group: t('form.role.groups.creative'),
      options: [
        { value: "ux_ui", text: t('form.role.options.ux_ui') },
        { value: "disenador_grafico", text: "Diseñador Gráfico" },
        { value: "musico_compositor", text: "Músico o Compositor" },
        { value: "especialista_sonido", text: "Especialista en Sonido" },
        { value: "contenido", text: t('form.role.options.content') },
        { value: "escritor_creativo", text: "Escritor Creativo" },
        { value: "artista_visual", text: "Artista Visual o Ilustrador" }
      ]
    },
    {
      group: t('form.role.groups.strategy'),
      options: [
        { value: "gestion_proyectos", text: t('form.role.options.project_management') },
        { value: "consultoria", text: t('form.role.options.consulting') },
        { value: "ventas", text: t('form.role.options.sales') },
        { value: "marketing_seo", text: "Especialista en Marketing Digital y SEO" },
        { value: "legal", text: "Especialista Legal y en Cumplimiento Normativo" },
        { value: "etica_ia", text: "Especialista en Ética de la IA" }
      ]
    },
    {
      group: "Roles Sugeridos",
      options: [
        { value: "realidad_virtual", text: "Especialista en Realidad Virtual (RV) o Realidad Aumentada (RA)" },
        { value: "gamificacion", text: "Experto en Gamificación" },
        { value: "coach_bienestar", text: "Coach de Bienestar o Mindfulness" },
        { value: "comunicacion_no_verbal", text: "Especialista en Comunicación No Verbal" },
        { value: "analista_datos", text: "Analista de Datos Creativo" },
        { value: "community_manager", text: "Community Manager Creativo" },
        { value: "narrativas_interactivas", text: "Especialista en Narrativas Interactivas" },
        { value: "otro", text: "Otro (confiamos en tus sugerencias)" }
      ]
    }
  ];

  const handleAcceptTerms = () => {
    setFormData(prev => ({
      ...prev,
      terms: true
    }));
    setShowTerms(false);
  };

  const handleCancelTerms = () => {
    setShowTerms(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="application-form" noValidate>
        <TextForm
          label={t('form.name.label')}
          name="nombre"
          value={formData.nombre}
          onChange={handleChange}
          required
          error={errors.nombre}
        />

        <TextForm
          label={t('form.age.label')}
          name="edad"
          type="number"
          value={formData.edad}
          onChange={handleChange}
          required
          error={errors.edad}
        />

        <TextForm
          label={t('form.email.label')}
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          error={errors.email}
        />

        <TextForm
          label={t('form.location.label')}
          name="zona"
          value={formData.zona}
          onChange={handleChange}
          required
          error={errors.zona}
        />

        <SelectForm
          label={t('form.role.label')}
          name="rol"
          value={formData.rol}
          onChange={handleChange}
          required
          placeholder={t('form.role.placeholder')}
          options={roleOptions}
          error={errors.rol}
        />

        <TextForm
          label={t('form.experience.label')}
          name="experiencia"
          value={formData.experiencia}
          onChange={handleChange}
          required
          multiline
          className="full-width"
          error={errors.experiencia}
        />

        <TextForm
          label={t('form.portfolio.label')}
          name="portafolio"
          type="url"
          value={formData.portafolio}
          onChange={handleChange}
          error={errors.portafolio}
        />

        <div className="form-group">
          <label>
            {t('form.languages.label')} <span className="required">*</span>
          </label>
          <MultipleSelect
            options={languageOptions}
            value={formData.idiomas}
            onChange={handleLanguageChange}
            placeholder={t('form.languages.placeholder')}
            error={errors.idiomas}
          />
        </div>

        <FileForm
          label={t('form.file.label')}
          name="archivo"
          onChange={handleChange}
          accept=".pdf,.doc,.docx"
          maxSize={5}
          className="medium-width"
        />
        <div className="helper-text">Tamaño máximo: 5 MB (PDF, DOC, DOCX)</div>

        <div className="terms">
          <input
            type="checkbox"
            name="terms"
            required
            checked={formData.terms}
            onChange={handleChange}
          />
          <label>
            {t('form.terms.label')}{' '}
            <button 
              type="button"
              onClick={() => setShowTerms(true)}
              className="terms-link-button"
            >
              {t('form.terms.link')}
            </button>
            <span className="required">*</span>
          </label>
        </div>
        {errors.terms && <div className="field-error">{errors.terms}</div>}

        {submitError && (
          <div className="error-message">
            {submitError}
          </div>
        )}

        <button 
          type="submit" 
          className="submit-button" 
          disabled={isSubmitting}
        >
          {isSubmitting ? t('form.submitting') : t('form.submit')}
        </button>
      </form>

      <Modal 
        isOpen={showTerms} 
        onClose={handleCancelTerms}
      >
        <Terms 
          onAccept={handleAcceptTerms}
          onCancel={handleCancelTerms}
        />
      </Modal>

      {showSuccess && (
        <Modal isOpen={showSuccess} onClose={() => navigate('/')}>
          <div className="success-message">
            <h2>{t('form.success.title')}</h2>
            <p>{t('form.success.emailSent')}</p>
            <p className="email-highlight">{submittedEmail}</p>
            <p>{t('form.success.checkInbox')}</p>
            <p>{t('form.success.checkSpam')}</p>
            <div className="next-steps">
              <h3>{t('form.success.nextSteps.title')}</h3>
              <p>1. {t('form.success.nextSteps.step1')}</p>
              <p>2. {t('form.success.nextSteps.step2')}</p>
              <p>3. {t('form.success.nextSteps.step3')}</p>
            </div>
            <button 
              className="submit-button" 
              onClick={() => navigate('/')}
            >
              {t('common.backToHome')}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ApplicationForm;